import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { Tween, ScrollTrigger } from "react-gsap";
import { useSelector } from "react-redux";

import Link from "../Global/Link";
import SmartText from "../../utils/TextHandler";
import SmartColour from "../../utils/ColourHandler";
import ImageHandler from "../../utils/ImageHandler";
import SocialLinks from "../Global/SocialLinks";
import addToMailchimp from "gatsby-plugin-mailchimp";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const SectionCommunity = (props) => {
  const { doc, location } = props;
  const colour = SmartColour(doc.colour);

  const country = useSelector((state) => state.global.country);

  const [socialLinks, setSocialLinks] = useState([]);

  let siteSettings = null;

  const siteData = useStaticQuery(graphql`
    {
      storyblokEntry(slug: { eq: "settings" }) {
        content
      }
    }
  `);

  siteSettings = JSON.parse(siteData.storyblokEntry.content);

  useEffect(() => {
    if (country === "zh") {
      setSocialLinks([
        { iconName: "weibo", link: siteSettings?.weibo },
        { iconName: "bilibili", link: siteSettings?.bilibili },
        { iconName: "", link: siteSettings?.heybox, isHeybox: true },
        { iconName: "tiktok", link: siteSettings?.douyin },
      ]);
    } else {
      setSocialLinks([
        { iconName: "facebook-f", link: siteSettings?.facebook },
        { iconName: "twitter", link: siteSettings?.twitter },
        { iconName: "instagram", link: siteSettings?.instagram },
        { iconName: "tiktok", link: siteSettings?.tiktok },
        { iconName: "discord", link: siteSettings?.discord },
      ]);
    }
  }, [
    location,
    country,
    siteSettings?.weibo,
    siteSettings?.bilibili,
    siteSettings?.heybox,
    siteSettings?.facebook,
    siteSettings?.twitter,
    siteSettings?.instagram,
    siteSettings?.tiktok,
    siteSettings?.discord,
    siteSettings?.douyin,
  ]);

  const [formSent, setFormSent] = useState(false);

  const YupSchema = Yup.object().shape({
    email: Yup.string().email(doc.error).required(doc.error),
  });

  return (
    <section
      id={doc.id && doc.id}
      className={`brand-section py-5 py-lg-10 d-flex flex-column justify-content-center align-items-center brand-background-${colour.background} brand-text-${colour.text} brand-background-cover`}
      style={
        doc.background && doc.background.filename
          ? {
              backgroundImage: `url(${ImageHandler(
                doc.background.filename,
                "1920x1080"
              )})`,
            }
          : {}
      }
    >
      <ScrollTrigger start="0 bottom" end="0 top" scrub>
        <div className="container">
          {doc.title && (
            <Tween from={{ y: "300px" }} to={{ y: "-100px" }}>
              <div className="row justify-content-center text-center">
                <div className="col-12">
                  <div className="p-5">
                    {doc.title && (
                      <h1
                        className={`brand-font mb-3 brand-text-dirty brand-text-${colour.headline} brand-font-responsive`}
                      >
                        {doc.title}
                      </h1>
                    )}

                    {formSent === true ? (
                      <div className="py-4 text-center fs-4">
                        {doc.thanks && doc.thanks}
                      </div>
                    ) : (
                      <>
                        {doc.body && <SmartText>{doc.body}</SmartText>}

                        <Formik
                          initialValues={{
                            email: "",
                          }}
                          validationSchema={YupSchema}
                          onSubmit={(values, { setSubmitting }) => {
                            setTimeout(() => {
                              setSubmitting(false);
                              addToMailchimp(values.email) // listFields are optional if you are only capturing the email address.
                                .then((data) => {
                                  setFormSent(true);
                                })
                                .catch(() => {
                                  // unnecessary because Mailchimp only ever
                                  // returns a 200 status code
                                });
                            }, 400);
                          }}
                        >
                          {({ errors, touched, isSubmitting }) => (
                            <Form>
                              <div className="row justify-content-center py-3 py-md-5">
                                {errors.email && touched.email ? (
                                  <div className="col-12">
                                    <div className="text-danger mb-3">
                                      {errors.email}
                                    </div>
                                  </div>
                                ) : null}
                                <div className="col-12 col-xl-7">
                                  <Field
                                    type="email"
                                    name="email"
                                    placeholder={doc.label}
                                    className="form-control form-control-lg brand-input"
                                    id="formEmail"
                                  />
                                </div>

                                <div className="col-12 col-md-auto py-3 py-xl-0">
                                  <Link
                                    button="real"
                                    icon={
                                      isSubmitting ? "spinner" : "chevron-right"
                                    }
                                    disabled={isSubmitting}
                                  >
                                    {doc.button}
                                  </Link>
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>

                        {doc.legal && (
                          <SmartText containerClassName="small">
                            {doc.legal}
                          </SmartText>
                        )}
                      </>
                    )}
                    <SocialLinks
                      className="d-flex flex-row justify-content-center mt-5"
                      itemClassName="mx-2 fs-2 brand-background-white brand-link-black rounded rounded-circle ratio ratio-1x1 brand-social-icon"
                      links={socialLinks}
                    />
                  </div>
                </div>
              </div>
            </Tween>
          )}
        </div>
      </ScrollTrigger>
    </section>
  );
};

export default SectionCommunity;

SectionCommunity.propTypes = {
  doc: PropTypes.object,
};

SectionCommunity.defaultProps = {
  doc: null,
};
